import { useEffect, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from './lib/supabase.ts';
import { isPasswordSetLanding } from './lib/authLanding.ts';
import { friendlyError } from './lib/friendlyError.ts';
import { btnGhost } from './lib/ui.ts';
import type { Role } from './lib/types.ts';
import { ThemeToggle } from './components/ThemeToggle.tsx';
import { Login } from './pages/Login.tsx';
import { UpdatePassword } from './pages/UpdatePassword.tsx';
import { AdminDashboard } from './pages/AdminDashboard.tsx';
import { DesignerDashboard } from './pages/DesignerDashboard.tsx';

// Public URL of the deployed component-library workbench (Cloudflare Access-locked).
// Opened in a new tab (not iframed — Access pages block framing). Hidden if unset.
const libraryUrl = import.meta.env.VITE_LIBRARY_URL as string | undefined;

export default function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [role, setRole] = useState<Role | null>(null);
  const [roleError, setRoleError] = useState<string | null>(null);
  // An invite (#28) or password-reset link lands here to SET a password. Recovery fires the
  // PASSWORD_RECOVERY event (handled below); an invite fires only SIGNED_IN, so we seed from
  // the URL type captured at load (authLanding.ts) to route both to UpdatePassword.
  const [recovery, setRecovery] = useState(() => isPasswordSetLanding());
  const [loading, setLoading] = useState(true);
  // Admins get two tabs: Admin (cockpit) and Design (the guided wizard, reused).
  // Persisted so a reload returns to the tab you were on (not always Admin).
  const [adminView, setAdminView] = useState<'admin' | 'design'>(
    () => (localStorage.getItem('alchmy.adminView') as 'admin' | 'design' | null) ?? 'admin',
  );
  useEffect(() => {
    localStorage.setItem('alchmy.adminView', adminView);
  }, [adminView]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      // Arriving via a password-reset email puts us in recovery mode: show the
      // "set a new password" screen instead of the app.
      if (event === 'PASSWORD_RECOVERY') setRecovery(true);
      setSession(s);
      setRole(null);
      setRoleError(null);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  // Resolve role client-side from the caller's own `profiles` row. RLS lets a
  // user read their own row (id = auth.uid()), so no server round-trip is needed
  // just to learn the role — and it never hangs silently.
  useEffect(() => {
    if (!session || recovery) return;
    let cancelled = false;
    setRoleError(null);
    void (async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', session.user.id)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        setRoleError(friendlyError(error, error.message));
        return;
      }
      if (!data) {
        setRoleError(
          'Signed in, but no profile is provisioned for this account. Ask Neil to grant access.',
        );
        return;
      }
      setRole(data.role as Role);
    })();
    return () => {
      cancelled = true;
    };
  }, [session, recovery]);

  if (loading) return <Centered>Loading…</Centered>;
  if (recovery) return <UpdatePassword onDone={() => setRecovery(false)} />;
  if (!session) return <Login />;

  if (roleError) {
    return (
      <Centered>
        <div className="max-w-sm space-y-3 text-center">
          <p className="text-sm text-danger">{roleError}</p>
          <button
            className={btnGhost}
            onClick={() => supabase.auth.signOut()}
          >
            Sign out
          </button>
        </div>
      </Centered>
    );
  }
  if (!role) return <Centered>Resolving access…</Centered>;

  return (
    <div className="min-h-screen text-ink">
      <header className="flex items-center justify-between border-b border-line bg-paper px-8 py-4">
        <div className="flex items-center gap-2">
          {/* Brand lockup — the Sorts Mill Goudy wordmark (via font-display), uppercase (brand §1). */}
          <span className="font-display text-2xl uppercase tracking-[-0.02em] leading-none text-ink">Alchmy Build</span>
          <span className="ml-1 rounded bg-paper-2 px-2 py-0.5 text-xs uppercase tracking-wide text-ink-2">
            {role}
          </span>
        </div>
        <div className="flex items-center gap-6">
          <ThemeToggle />
          {libraryUrl && (
            <a
              href={libraryUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-ink-2 hover:text-ink"
            >
              Component Library ↗
            </a>
          )}
          <button
            className="text-sm text-ink-2 hover:text-ink"
            onClick={() => supabase.auth.signOut()}
          >
            Sign out ({session.user.email})
          </button>
        </div>
      </header>
      <main className="mx-auto max-w-5xl p-8">
        {role === 'admin' ? (
          <div className="space-y-8">
            {/* Segmented-pill tabs (brand §6): a sunken track, the active tab a raised tile. */}
            <nav className="nm-inset inline-flex gap-1 self-start rounded-full bg-paper p-1">
              <Tab label="Admin" active={adminView === 'admin'} onClick={() => setAdminView('admin')} />
              <Tab label="Design" active={adminView === 'design'} onClick={() => setAdminView('design')} />
            </nav>
            {adminView === 'admin' ? <AdminDashboard /> : <DesignerDashboard viewerRole="admin" />}
          </div>
        ) : (
          <DesignerDashboard viewerRole="designer" />
        )}
      </main>
    </div>
  );
}

function Tab({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full px-4 py-1.5 text-sm font-medium transition ${
        active ? 'nm-tile text-ink' : 'text-ink-2 hover:text-ink'
      }`}
    >
      {label}
    </button>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center text-ink-2">
      {children}
    </div>
  );
}
